import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IAddBreakpointAction,
  IAddExpressionAction,
  IAddSessionAction,
  IBreakpointDecoration,
  IClearBreakpointsAction,
  IDebuggerData,
  IDebuggerSession,
  IRemoveBreakpointAction,
  IRemoveExpressionAction,
  IRemoveSessionAction,
  IRenameExpressionAction,
  ISaveExpressionsAction,
  ISetActiveLineAction,
  ISetBreakpointsAction,
  ISetExpressionAction,
  ISetSourceContentAction,
  ISetCallChainVariablesAction,
  IUpdateSessionAction,
  ISetExpressionVariablesAction,
  ISessionVariable,
  EDebuggerState,
  IDebuggerMeta,
  ISetDebuggerMetaAction,
  EDebuggerErrorType,
  IExpression,
  IBreakpoint,
  IRemoveAllExpressionsAction,
  IUpdateExpressionAtAction,
  IClearBreakpointsExceptAction,
  IToggleBreakpointAction,
  ISetCommonVariablesAction,
  IToggleBreakpointsInFileAction,
  IUpdateBreakpointsAction,
} from './types';
import { findOne, findSession } from '~store/selectors/debugger';
import { getDebuggerTabPath } from './utils';
import { isNumber } from 'lodash';
import { getCachedState } from '~store/utils';
import { wipeDebuggerSessions } from './thunks';
import { FileFolderType } from '@qodly/shared';

export interface IDebuggerState {
  meta: IDebuggerMeta;
  list: IDebuggerData[];
  sessions: IDebuggerSession[];
  expressions: IExpression[];
}

function setBreakpoint(item: IDebuggerData, breakpoint: IBreakpointDecoration) {
  const { line, lineInEditor, offset, type = 'saved' } = breakpoint;
  item.breakpoints[type].locations.push({ line, lineInEditor });
  if (isNumber(offset)) {
    item.breakpoints[type].offset = offset;
  }
}

function setBreakpoints(
  item: IDebuggerData,
  breakpoints: IBreakpoint[],
  key: keyof IDebuggerData['breakpoints'],
  offset?: number,
  merge: boolean = false,
) {
  if (merge) {
    // getting the real index for each breakpoint location
    const indexMap = new Map<number, number>();
    let indexCounter = 0;
    item.breakpoints[key].locations.forEach((location, initialIndex) => {
      if (location.disabled) {
        indexMap.set(initialIndex, -1); // mark disabled items as -1
      } else {
        indexMap.set(initialIndex, indexCounter++); // use current indexCounter value and increment it for next values
      }
    });
    // merging breakpoints based on real index
    item.breakpoints[key].locations = item.breakpoints[key].locations.map(
      (location, index) => {
        const realIndex = indexMap.get(index);
        if (
          !location.disabled &&
          isNumber(realIndex) &&
          breakpoints[realIndex]
        ) {
          return {
            ...location,
            ...breakpoints[realIndex],
          };
        }
        return location;
      },
    );
  } else {
    item.breakpoints[key].locations = breakpoints;
  }
  if (isNumber(offset)) {
    item.breakpoints[key].offset = offset;
  }
}

function getNewListItem({
  filePath,
  fileType,
  methodName,
  className,
  ext,
  source,
}: Omit<IDebuggerData, 'breakpoints'>): IDebuggerData {
  return {
    filePath,
    fileType,
    methodName,
    className,
    ext,
    source,
    breakpoints: {
      draft: {
        locations: [],
        offset: 0,
      },
      saved: {
        locations: [],
        offset: 0,
      },
    },
  };
}

function setVariables<T = ISessionVariable>(
  variables: T[],
  newVariable: T,
  uniqueKey: keyof T,
) {
  let found = false;
  const updatedVariables = variables.map((variable) => {
    if (variable[uniqueKey] !== newVariable[uniqueKey]) {
      return variable;
    }
    found = true;
    return { ...variable, ...newVariable };
  });
  return found ? updatedVariables : [...updatedVariables, newVariable];
}

function prepareExpressionsForSave(expressions: IExpression[]) {
  return expressions.map(({ label }) => ({ label }));
}

const stateSlice = createSlice({
  name: 'debugger',
  initialState: (): IDebuggerState => {
    const {
      debugger: debuggerState = {
        meta: {
          state: EDebuggerState.DETACHED,
        },
        list: [],
        sessions: [],
        expressions: [],
      },
    } = getCachedState();

    return {
      meta: {
        ...debuggerState.meta,
        state:
          debuggerState.meta?.state === EDebuggerState.ATTACHED
            ? debuggerState.meta?.state
            : EDebuggerState.DETACHED,
      },
      list: debuggerState.list || [],
      sessions: debuggerState.sessions || [],
      expressions: debuggerState.expressions || [],
    };
  },
  reducers: {
    addBreakpointDecoration(state, action: IAddBreakpointAction) {
      const {
        filePath,
        fileType,
        methodName,
        className,
        ext,
        source,
        breakpoint,
      } = action.payload;
      const found = findOne(state.list, filePath, methodName, className);
      if (found) {
        setBreakpoint(found, breakpoint);
      } else {
        if (
          fileType === FileFolderType.CLASS &&
          breakpoint.offset === undefined
        ) {
          throw new Error(
            `Cannot add a breakpoint on a function without offset. Try editing the ${className} class.`,
          );
        }
        const item = getNewListItem({
          filePath,
          fileType,
          methodName,
          className,
          ext,
          source,
        });
        setBreakpoint(item, breakpoint);
        state.list.push(item);
      }
    },
    removeBreakpointDecoration(state, action: IRemoveBreakpointAction) {
      const {
        filePath,
        methodName,
        line,
        offset = 0,
        type = 'saved',
      } = action.payload;
      // case of class method
      if (filePath.startsWith('Classes/') /* || offset > 0 */) {
        const items = state.list.filter((item) => item.filePath === filePath);
        if (items.length > 0) {
          const lineWithOffset = line + offset;
          items.forEach((item) => {
            const itemOffset = item.breakpoints[type].offset || 0;
            item.breakpoints[type].locations = item.breakpoints[
              type
            ].locations.filter(
              (location) => location.line + itemOffset !== lineWithOffset,
            );
            if (type === 'saved') {
              // remove breakpoint from draft state when it is removed from saved state
              item.breakpoints.draft.locations =
                item.breakpoints.draft.locations.filter(
                  (location) => location.line + itemOffset !== lineWithOffset,
                );
            }
          });
        }
      } else {
        // case of project method
        const found = findOne(state.list, filePath, methodName);
        if (found) {
          found.breakpoints[type].locations = found.breakpoints[
            type
          ].locations.filter((location) => location.line !== line);
          if (type === 'saved') {
            // remove breakpoint from draft state when it is removed from saved state
            found.breakpoints.draft.locations =
              found.breakpoints.draft.locations.filter(
                (location) => location.line !== line,
              );
          }
        }
      }
    },
    setBreakpointsDecoration(state, action: ISetBreakpointsAction) {
      const {
        filePath,
        fileType,
        methodName,
        className,
        ext,
        source,
        breakpoints,
        offset,
        type = 'saved',
        merge = false,
        clearDraft,
      } = action.payload;
      const found = findOne(state.list, filePath, methodName, className);
      if (found) {
        setBreakpoints(found, breakpoints, type, offset, merge);
        if (clearDraft && type !== 'draft') {
          found.breakpoints.draft.locations = [];
        }
      } else {
        const item = getNewListItem({
          filePath,
          fileType,
          methodName,
          className,
          ext,
          source,
        });
        setBreakpoints(item, breakpoints, type, offset);
        state.list.push(item);
      }
    },
    updateBreakpointsDecoration(state, action: IUpdateBreakpointsAction) {
      const {
        filePath,
        methodName,
        className,
        breakpoints,
        type = 'saved',
        merge = false,
      } = action.payload;
      const found = findOne(state.list, filePath, methodName, className);
      if (found) {
        setBreakpoints(found, breakpoints, type, undefined, merge);
      }
    },
    clearBreakpoints(state, action: IClearBreakpointsAction) {
      const { filePath, type = 'saved' } = action.payload;
      state.list = state.list.map((item) => ({
        ...item,
        breakpoints:
          item.filePath === filePath
            ? {
                draft:
                  !type || type === 'draft' || type === 'any'
                    ? { locations: [], offset: 0 }
                    : item.breakpoints.draft,
                saved:
                  !type || type === 'saved' || type === 'any'
                    ? { locations: [], offset: 0 }
                    : item.breakpoints.saved,
              }
            : item.breakpoints,
      }));
    },
    clearBreakpointsExcept(state, action: IClearBreakpointsExceptAction) {
      const { exceptedFilePath, type = 'saved' } = action.payload;
      state.list = state.list.map((item) => ({
        ...item,
        breakpoints:
          item.filePath !== exceptedFilePath
            ? {
                draft:
                  !type || type === 'draft' || type === 'any'
                    ? { locations: [], offset: 0 }
                    : item.breakpoints.draft,
                saved:
                  !type || type === 'saved' || type === 'any'
                    ? { locations: [], offset: 0 }
                    : item.breakpoints.saved,
              }
            : item.breakpoints,
      }));
    },
    clearAllBreakpoints(state) {
      state.list = state.list.map((item) => ({
        ...item,
        breakpoints: {
          draft: { locations: [], offset: 0 },
          saved: { locations: [], offset: 0 },
        },
      }));
    },
    toggleBreakpoint(state, action: IToggleBreakpointAction) {
      const {
        filePath,
        methodName,
        line,
        offset = 0,
        disabled,
      } = action.payload;
      // case of class method
      if (filePath.startsWith('Classes/')) {
        const items = state.list.filter((item) => item.filePath === filePath);
        if (items.length > 0) {
          const lineWithOffset = line + offset;
          items.forEach((item) => {
            const itemOffset = item.breakpoints.saved.offset || 0;
            item.breakpoints.saved.locations =
              item.breakpoints.saved.locations.map((location) => {
                if (location.line + itemOffset === lineWithOffset) {
                  return {
                    ...location,
                    disabled,
                  };
                }
                return location;
              });
          });
        }
      } else {
        // case of project method
        const found = findOne(state.list, filePath, methodName);
        if (found) {
          found.breakpoints.saved.locations =
            found.breakpoints.saved.locations.map((location) => {
              if (location.line === line) {
                return {
                  ...location,
                  disabled,
                };
              }
              return location;
            });
        }
      }
    },
    toggleBreakpointsInFile(state, action: IToggleBreakpointsInFileAction) {
      const { filePath, methodName, disabled } = action.payload;
      // case of class method
      if (filePath.startsWith('Classes/')) {
        const items = state.list.filter((item) => item.filePath === filePath);
        if (items.length > 0) {
          items.forEach((item) => {
            item.breakpoints.saved.locations =
              item.breakpoints.saved.locations.map((location) => {
                return {
                  ...location,
                  disabled,
                };
              });
          });
        }
      } else {
        // case of project method
        const found = findOne(state.list, filePath, methodName);
        if (found) {
          found.breakpoints.saved.locations =
            found.breakpoints.saved.locations.map((location) => {
              return {
                ...location,
                disabled,
              };
            });
        }
      }
    },
    disableAllBreakpoints(state) {
      state.list = state.list.map((item) => ({
        ...item,
        breakpoints: {
          ...item.breakpoints,
          saved: {
            ...item.breakpoints.saved,
            locations: item.breakpoints.saved.locations.map((location) => ({
              ...location,
              disabled: true,
            })),
          },
        },
      }));
    },
    enableAllBreakpoints(state) {
      state.list = state.list.map((item) => ({
        ...item,
        breakpoints: {
          ...item.breakpoints,
          saved: {
            ...item.breakpoints.saved,
            locations: item.breakpoints.saved.locations.map((location) => ({
              ...location,
              disabled: false,
            })),
          },
        },
      }));
    },
    setActiveLine(state, action: ISetActiveLineAction) {
      const { threadId, line, highlight } = action.payload;
      const session = findSession(state.sessions, threadId);
      if (session) {
        session.activeLine = line;
        if (highlight) {
          session.highlightedLines = {
            start: line,
            end: line,
          };
        }
      }
    },
    addExpression(state, action: IAddExpressionAction) {
      const { threadId, label, save } = action.payload;
      const session = findSession(state.sessions, threadId);
      if (session && session.stopped) {
        session.expressions = [...(session.expressions || []), { label }];
        if (save) {
          state.expressions = prepareExpressionsForSave(session.expressions);
        }
      }
    },
    removeExpression(state, action: IRemoveExpressionAction) {
      const { threadId, index, save } = action.payload;
      const session = findSession(state.sessions, threadId);
      if (session) {
        session.expressions = session.expressions.filter((_, i) => i !== index);
        if (save) {
          state.expressions = prepareExpressionsForSave(session.expressions);
        }
      }
    },
    // remove all expressions from the current session
    removeAllExpressions(state, action: IRemoveAllExpressionsAction) {
      const { threadId, save } = action.payload;
      const session = findSession(state.sessions, threadId);
      if (session) {
        session.expressions = [];
        if (save) {
          state.expressions = [];
        }
      }
    },
    renameExpression(state, action: IRenameExpressionAction) {
      const { threadId, index, replacement, save } = action.payload;
      const session = findSession(state.sessions, threadId);
      if (session && session.expressions[index]) {
        session.expressions[index].label = replacement;
        if (save) {
          state.expressions = prepareExpressionsForSave(session.expressions);
        }
      }
    },
    setExpression(state, action: ISetExpressionAction) {
      const { threadId, expression } = action.payload;
      const session = findSession(state.sessions, threadId);
      if (session) {
        const index =
          expression.index >= 0
            ? expression.index
            : session.expressions.length > 0
              ? session.expressions.length - 1
              : 0;
        const found = session.expressions[index];
        if (found) {
          found.result = expression.result;
          found.nakedResult = expression.nakedResult;
          found.variablesReference = expression.variablesReference || 0;
          found.presentationHint = expression.presentationHint;
        } else {
          session.expressions.push(expression);
        }
      }
    },
    updateExpressionAt(state, action: IUpdateExpressionAtAction) {
      const { threadId, index, value } = action.payload;
      const session = findSession(state.sessions, threadId);
      if (session) {
        const found = session.expressions[index];
        if (found) {
          found.result = `"${value}"`;
          found.nakedResult = value;
        }
      }
    },
    saveExpressions(state, action: ISaveExpressionsAction) {
      const { expressions } = action.payload;
      state.expressions = prepareExpressionsForSave(expressions);
    },
    addSession(state, action: IAddSessionAction) {
      const {
        threadId,
        channelId,
        stackFrames,
        exceptions = [],
      } = action.payload;
      const session = findSession(state.sessions, threadId);
      const currentStackFrame = stackFrames[0];
      const { expressions = [] } = state;
      if (session) {
        session.expressions = expressions;
      } else {
        const tabPath = getDebuggerTabPath(threadId);
        state.sessions.push({
          tabPath,
          threadId,
          channelId,
          stackFrames,
          currentStackFrame,
          exceptions,
          expressions,
          variables: {
            callchain: [],
            expressions: [],
            local: [],
            currentLine: [],
          },
          highlightedLines: null,
          activeLine: null,
          stopped: true,
        });
      }
    },
    updateSession(state, action: IUpdateSessionAction) {
      const { threadId, changes } = action.payload;
      state.sessions = state.sessions.map((session) =>
        session.threadId === threadId
          ? {
              ...session,
              ...changes,
            }
          : session,
      );
    },
    removeSession(state, action: IRemoveSessionAction) {
      const { threadId } = action.payload;
      state.sessions = state.sessions.filter(
        (session) => session.threadId !== threadId,
      );
    },
    removeSessionByIndex(state, action: PayloadAction<number>) {
      const index = action.payload;
      state.sessions.splice(index, 1);
    },
    setSessions(state, action: PayloadAction<IDebuggerSession[]>) {
      const sessions = action.payload;
      state.sessions = sessions;
    },
    setCommonVariables(state, action: ISetCommonVariablesAction) {
      const {
        key,
        threadId,
        frameId,
        variablesReference,
        variables: items,
      } = action.payload;
      state.sessions = state.sessions.map((session) =>
        session.threadId === threadId
          ? {
              ...session,
              variables: {
                ...session.variables,
                [key]: setVariables(
                  session.variables?.[key],
                  { frameId, variablesReference, items },
                  'frameId',
                ),
              },
            }
          : session,
      );
    },
    setCallChainVariables(state, action: ISetCallChainVariablesAction) {
      const {
        threadId,
        frameId,
        variablesReference,
        variables: items,
      } = action.payload;
      state.sessions = state.sessions.map((session) =>
        session.threadId === threadId
          ? {
              ...session,
              variables: {
                ...session.variables,
                callchain: setVariables(
                  session.variables?.callchain,
                  { frameId, variablesReference, items },
                  'frameId',
                ),
              },
            }
          : session,
      );
    },
    setExpressionVariables(state, action: ISetExpressionVariablesAction) {
      const { threadId, variablesReference, variables: items } = action.payload;
      // TODO: This requires cleanup of unused variables references
      state.sessions = state.sessions.map((session) =>
        session.threadId === threadId
          ? {
              ...session,
              variables: {
                ...session.variables,
                expressions: setVariables(
                  session.variables?.expressions,
                  { variablesReference, items },
                  'variablesReference',
                ),
              },
            }
          : session,
      );
    },
    setSourceContent(state, action: ISetSourceContentAction) {
      const { threadId, content } = action.payload;
      const session = findSession(state.sessions, threadId);
      if (session) {
        session.currentStackFrame.source = {
          ...session.currentStackFrame.source,
          content,
        };
      }
    },
    setDebuggerMeta(state, action: ISetDebuggerMetaAction) {
      if (action.payload.state === EDebuggerState.ERROR) {
        // set error
        const {
          error = {
            code: null,
            reason: '',
            type: EDebuggerErrorType.SOCKET_ERROR,
          },
        } = action.payload;

        state.meta.error = {
          ...state.meta.error,
          ...error,
          // update error time (to force re-rendering)
          time: Date.now(),
        };

        // ignore socket closure if detached
        if (
          error?.type === EDebuggerErrorType.SOCKET_CLOSURE &&
          error?.code === 1000 &&
          !error?.reason &&
          state.meta.state === EDebuggerState.DETACHED
        )
          return;
      }

      state.meta.state = action.payload.state;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(wipeDebuggerSessions.fulfilled, (state) => {
      state.sessions = [];
    });
  },
});

export const {
  addBreakpointDecoration,
  removeBreakpointDecoration,
  setBreakpointsDecoration,
  updateBreakpointsDecoration,
  clearBreakpoints,
  clearBreakpointsExcept,
  clearAllBreakpoints,
  toggleBreakpoint,
  toggleBreakpointsInFile,
  disableAllBreakpoints,
  enableAllBreakpoints,
  setActiveLine,
  addExpression,
  renameExpression,
  removeExpression,
  removeAllExpressions,
  setExpression,
  updateExpressionAt,
  saveExpressions,
  addSession,
  updateSession,
  removeSession,
  removeSessionByIndex,
  setSessions,
  setCallChainVariables,
  setExpressionVariables,
  setCommonVariables,
  setSourceContent,
  setDebuggerMeta,
} = stateSlice.actions;

export default stateSlice;
